import { useState, useCallback } from 'react';

/**
 * Custom hook for managing selection state with toggle functionality
 * Supports both single-select and multi-select modes
 */
export const useSelectionState = (isMultiSelect: boolean = false) => {
    const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
    const [optionKeys, setOptionKeys] = useState<Map<string, string>>(new Map());
    const [renderKey, setRenderKey] = useState(0);

    const handleOptionClick = useCallback((option: string, key?: string) => {
        // Use functional setState to avoid dependency on selectedOptions
        setSelectedOptions(prev => {
            const isSelected = prev.includes(option);
            
            if (isSelected) {
                // Deselect: remove this option
                setOptionKeys(prevKeys => {
                    const newKeys = new Map(prevKeys);
                    newKeys.delete(option);
                    return newKeys;
                });
                return prev.filter(item => item !== option);
            } else {
                // Select: add or replace
                if (key) {
                    setOptionKeys(prevKeys => {
                        const newKeys = new Map(prevKeys);
                        if (!isMultiSelect) {
                            newKeys.clear(); // Clear all keys in single-select
                        }
                        newKeys.set(option, key);
                        return newKeys;
                    });
                } else if (!isMultiSelect) {
                    setOptionKeys(new Map()); // Clear all keys in single-select
                }
                
                if (!isMultiSelect) {
                    // Single-select: replace all with just this one
                    return [option];
                } else {
                    // Multi-select: add to existing
                    return [...prev, option];
                }
            }
        });
        
        // Force re-render (reset to 0 after 10000 to prevent overflow)
        setRenderKey(k => (k + 1) % 10000);
    }, [isMultiSelect]);

    const resetSelections = useCallback(() => {
        setSelectedOptions([]);
        setOptionKeys(new Map());
        setRenderKey(0);
    }, []);

    const setSelectionsFromJSON = useCallback((selectJson: any) => {
        if (selectJson !== null && selectJson !== undefined) {
            if (Array.isArray(selectJson)) {
                setSelectedOptions(selectJson);
            } else if (typeof selectJson === 'string') {
                setSelectedOptions([selectJson]);
            }
        } else {
            setSelectedOptions([]);
            setOptionKeys(new Map());
            setRenderKey(0);
        }
    }, []);

    return {
        selectedOptions,
        optionKeys,
        renderKey,
        handleOptionClick,
        resetSelections,
        setSelectionsFromJSON
    };
};

